import { useLazyQuery, useQuery } from "@apollo/client";
import { IonCol, IonGrid, IonRow } from "@ionic/react";
import { useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import { StringParam, useQueryParams } from "use-query-params";

import CircleButton from "@components/buttons/CircleButton";
import DirectionButton from "@components/buttons/DirectionButton";
import CardBase from "@components/cards/CardBase";
import CarePlanCard from "@components/CarePlanCard";
import HandleQuery from "@components/HandleQuery";
import styles from "@components/libraries/Library.module.css";
import Modal from "@components/Modal";
import Icon from "@components/text/Icon";
import BodyCopy from "@components/typography/BodyCopy";
import ButtonLink from "@components/typography/ButtonLink";
import Heading1 from "@components/typography/Heading1";
import Heading2 from "@components/typography/Heading2";
import useCreateGoal from "@hooks/mutations/useCreateGoal";
import useMyClient from "@hooks/useMyClient";
import useNodesFromConnection from "@hooks/useNodesFromConnection";
import useOpener from "@hooks/useOpener";
import useSanitizeHtml from "@hooks/useSanitizeHtml";
import useScrollPosition from "@hooks/useScrollPosition";
import { CarePlanSectionEnum, ScrollPositionPageTypeEnum } from "@typing/Enums";
import {
  FocusAreaEnum,
  GetGoalTemplatesBasicDocument,
  GetGoalTemplatesBasicQuery,
  GetGoalTemplatesBasicQueryVariables,
  GetJourneyWithPlanDocument,
  GetJourneyWithPlanQuery,
  GetJourneyWithPlanQueryVariables,
  GoalTemplateKindEnum,
  GoalTemplateSortByEnum,
  SortDirectionEnum,
  UserTypeEnum
} from "@typing/Generated";
import { Direction } from "@typing/Models";
import { groupByFocusArea } from "@utils/modelUtils";
import { isMobile } from "@utils/platformUtils";
import { journeyRoute } from "@utils/routeUtils";

type TemplateItemProps = {
  title: string;
  type: "article" | "worksheet" | "task";
};

const TemplateItem = ({ title, type }: TemplateItemProps) => (
  <CarePlanCard
    body={
      <div className="flex justify-between items-center gap-10">
        <BodyCopy>
          {type === "task" ? (
            title
          ) : (
            <FormattedMessage
              id={type === "article" ? "models.article.title" : "models.worksheet.title"}
              values={{
                title
              }}
            />
          )}
        </BodyCopy>
      </div>
    }
    checkboxProps={{ checked: false, disabled: true, name: "template-item-checkbox" }}
    className={styles.card}
    popoverItems={[]}
  />
);

type Journey = GetJourneyWithPlanQuery["journey"];

type Props = {
  handleCurrentSectionChange?: (section: CarePlanSectionEnum, direction: Direction) => void;
  journeyId: string;
};

type GoalTemplate = NonNullable<
  NonNullable<NonNullable<GetGoalTemplatesBasicQuery["goalTemplates"]["edges"]>[number]>["node"]
>;

type SectionProps = {
  focusArea: FocusAreaEnum;
  goalTemplates: GoalTemplate[];
  handleGoalCreate: (templateId: string) => void;
  handleSelectGoalTemplate: (goalTemplateId: string) => void;
  journey: Journey;
  loadingGoalTemplateId: string | null;
};

const FocusAreaSection = ({
  focusArea,
  goalTemplates,
  handleGoalCreate,
  handleSelectGoalTemplate,
  journey,
  loadingGoalTemplateId
}: SectionProps) => (
  <div className={styles.section}>
    <div className="margin-4-bottom">
      <Heading1 color="primary">
        <FormattedMessage id={`models.focusArea.${focusArea}`} />
      </Heading1>
    </div>
    <BodyCopy color="primary">
      <FormattedMessage id={`models.focusArea.descriptions.${focusArea}`} />
    </BodyCopy>

    <div className="flex flex-column gap-md ion-margin-top">
      {goalTemplates.map(goalTemplate => (
        <CardBase
          body={
            <IonGrid className="ion-no-padding">
              <IonRow className="ion-align-items-center">
                <IonCol size="auto">
                  <div className="ion-margin-end margin-4-top">
                    <Icon color="primary" icon="folderOpen" />
                  </div>
                </IonCol>
                <IonCol>
                  <Heading2 bold color="primary">
                    {goalTemplate.title}
                  </Heading2>
                </IonCol>
                <IonCol size="auto">
                  {journey.goals.some(goal => goal.goalTemplate?.id === goalTemplate.id) ? (
                    <Icon className={styles.check} color="success" icon="check" />
                  ) : (
                    <CircleButton
                      ariaLabelKey="accessibility.ariaLabels.add"
                      disabled={loadingGoalTemplateId === goalTemplate.id}
                      fill="clear"
                      icon="add"
                      onClick={e => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleGoalCreate(goalTemplate.id);
                      }}
                      size="medium"
                    />
                  )}
                </IonCol>
              </IonRow>
            </IonGrid>
          }
          key={goalTemplate.id}
          onClick={() => handleSelectGoalTemplate(goalTemplate.id)}
        />
      ))}
    </div>
  </div>
);

const GoalTemplatesLibrary = ({ handleCurrentSectionChange, journeyId }: Props) => {
  const [query] = useQueryParams({
    goalTemplateId: { ...StringParam, urlName: "goalTemplateId" }
  });

  const [selectedGoalTemplateId, setSelectedGoalTemplateId] = useState<string | null | undefined>(query.goalTemplateId);

  const goalTemplateOpener = useOpener(!!selectedGoalTemplateId);

  const [getGoalTemplates, getGoalTemplatesQuery] = useLazyQuery<
    GetGoalTemplatesBasicQuery,
    GetGoalTemplatesBasicQueryVariables
  >(GetGoalTemplatesBasicDocument);

  const getJourneyQuery = useQuery<GetJourneyWithPlanQuery, GetJourneyWithPlanQueryVariables>(
    GetJourneyWithPlanDocument,
    {
      variables: { id: journeyId }
    }
  );

  const [loadingId, setLoadingId] = useState<string | null>(null);
  const myClient = useMyClient();

  const createGoal = useCreateGoal({ journey: getJourneyQuery.data?.journey ?? null });

  const handleGoalCreate = (goalTemplateId: string) => {
    if (getJourneyQuery.data?.journey) {
      setLoadingId(goalTemplateId);
      createGoal({
        draft: myClient ? false : true,
        goalTemplateId,
        journeyId: getJourneyQuery.data.journey.id
      }).then(() => {
        setLoadingId(null);
      });
    }
  };

  useEffect(() => {
    getGoalTemplates({
      variables: {
        archived: false,
        journeyId,
        kind: GoalTemplateKindEnum.ALL,
        sortBy: GoalTemplateSortByEnum.TITLE,
        sortDirection: SortDirectionEnum.ASCENDING,
        usedInJourneyId: null
      }
    });
  }, [getGoalTemplates, journeyId]);

  useEffect(() => {
    if (!getGoalTemplatesQuery.loading && getGoalTemplatesQuery.data?.goalTemplates.pageInfo.hasNextPage) {
      getGoalTemplatesQuery.fetchMore({
        variables: { cursor: getGoalTemplatesQuery.data?.goalTemplates.pageInfo.endCursor ?? "" }
      });
    }
  }, [getGoalTemplatesQuery]);

  useScrollPosition(
    ScrollPositionPageTypeEnum.GOAL_TEMPLATES,
    !getGoalTemplatesQuery.loading && !getGoalTemplatesQuery.data?.goalTemplates.pageInfo.hasNextPage
  );

  const allGoalTemplates = useNodesFromConnection(getGoalTemplatesQuery.data?.goalTemplates);

  const goalTemplatesByFocusArea = groupByFocusArea(allGoalTemplates);

  const selectedGoalTemplate =
    allGoalTemplates.find(goalTemplate => goalTemplate.id === selectedGoalTemplateId) ?? null;

  const handleGoalTemplateSelect = (goalTemplateId: string) => {
    if (selectedGoalTemplateId === goalTemplateId) {
      setSelectedGoalTemplateId(null);
      goalTemplateOpener.close();
    } else {
      setSelectedGoalTemplateId(goalTemplateId);
      goalTemplateOpener.open();
    }
  };

  const sanitizedDescription = useSanitizeHtml({
    rawHtml: selectedGoalTemplate?.description,
    resource: selectedGoalTemplate
  });

  return (
    <div className={styles.mobileContainer}>
      <div className="margin-20-bottom ion-hide-lg-down">
        <DirectionButton
          direction="back"
          onClick={() => handleCurrentSectionChange?.(CarePlanSectionEnum.CARE_PLAN, "left")}
          routerLink={isMobile() ? undefined : journeyRoute({ journeyId, linkAfterJourney: "/care-plan", myClient })}
        />
      </div>

      <HandleQuery query={getJourneyQuery}>
        {journeyData => (
          <HandleQuery query={getGoalTemplatesQuery}>
            {() => (
              <>
                {Object.keys(goalTemplatesByFocusArea).map(focusArea => {
                  const goalTemplates = goalTemplatesByFocusArea[focusArea as FocusAreaEnum];
                  if (goalTemplates && goalTemplates.length > 0) {
                    return (
                      <FocusAreaSection
                        focusArea={focusArea as FocusAreaEnum}
                        goalTemplates={goalTemplates}
                        handleGoalCreate={handleGoalCreate}
                        handleSelectGoalTemplate={handleGoalTemplateSelect}
                        journey={journeyData.journey}
                        key={focusArea}
                        loadingGoalTemplateId={loadingId}
                      />
                    );
                  }
                  return <div key={focusArea} />;
                })}
              </>
            )}
          </HandleQuery>
        )}
      </HandleQuery>

      <Modal
        breakpoints={[0, 0.25, 0.5, 0.7, 1]}
        className={`${isMobile() ? undefined : "big"} gray-modal`}
        contentClassName={styles.modalContent}
        hideHeader={!isMobile()}
        initialBreakpoint={isMobile() ? 1 : undefined}
        onIonModalWillDismiss={() => setSelectedGoalTemplateId(null)}
        opener={goalTemplateOpener}
        title={selectedGoalTemplate?.title ?? ""}
      >
        {() => (
          <div className="align-center ion-margin-top">
            {selectedGoalTemplate && (
              <div>
                <div className="flex justify-between ion-hide-lg-down">
                  <div className={isMobile() ? "" : styles.fakeButton} />
                  <Heading1>{selectedGoalTemplate.title}</Heading1>
                  {!isMobile() && (
                    <CircleButton
                      ariaLabelKey="accessibility.ariaLabels.close"
                      fill="clear"
                      icon="times"
                      onClick={() => {
                        setSelectedGoalTemplateId(null);
                        goalTemplateOpener.close();
                      }}
                      size="medium"
                    />
                  )}
                </div>

                <div className="margin-10-top">
                  <BodyCopy>{sanitizedDescription}</BodyCopy>
                </div>
                <div className={`flex gap-10 flex-column ${styles.taskGap}`}>
                  {selectedGoalTemplate.carePlanTaskTemplates
                    .filter(t => t.userType === UserTypeEnum.EXPERT)
                    .map(carePlanTaskTemplate => (
                      <TemplateItem key={carePlanTaskTemplate.id} title={carePlanTaskTemplate.title} type="task" />
                    ))}
                  {selectedGoalTemplate.carePlanTaskTemplates
                    .filter(t => t.userType === UserTypeEnum.CLIENT)
                    .map(carePlanTaskTemplate => (
                      <TemplateItem key={carePlanTaskTemplate.id} title={carePlanTaskTemplate.title} type="task" />
                    ))}
                  {selectedGoalTemplate.articles.map(article => (
                    <TemplateItem key={article.id} title={article.title} type="article" />
                  ))}
                  {selectedGoalTemplate.worksheetTemplates.map(worksheetTemplate => (
                    <TemplateItem key={worksheetTemplate.id} title={worksheetTemplate.title} type="worksheet" />
                  ))}
                </div>
                <div className="margin-20-top">
                  <ButtonLink
                    onClick={() => {
                      handleGoalCreate(selectedGoalTemplate.id);
                      setSelectedGoalTemplateId(null);
                      goalTemplateOpener.close();
                    }}
                    stylized={false}
                  >
                    <div>
                      <Icon color="primary" icon="add" size="xlarge" />
                      <div className="margin-10-top">
                        <BodyCopy color="primary" size="small">
                          <FormattedMessage id="models.goal.add" />
                        </BodyCopy>
                      </div>
                    </div>
                  </ButtonLink>
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default GoalTemplatesLibrary;
